/** Job history page with DataTable. */

import { useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReplayIcon from "@mui/icons-material/Replay";
import { DataTable, useTableCollection, type Column, PageHeader, StatusChip } from "@selerity/ui";
import { useJobs } from "@/hooks/useJobs";

interface Job {
  jobId: string;
  name?: string;
  pluginId: string;
  pluginName?: string;
  jobType: string;
  status: string;
  createdAt: string;
}

export default function Jobs() {
  const { data, isLoading } = useJobs();
  const navigate = useNavigate();

  const jobs: Job[] = data?.pages?.flatMap((page) => page.items) ?? [];

  const columns: Column<Job>[] = [
    {
      id: "date",
      header: "Date",
      sortField: "createdAt",
      cell: (job) => new Date(job.createdAt).toLocaleString(),
    },
    {
      id: "name",
      header: "Name",
      sortField: "name",
      cell: (job) => job.name || job.pluginName || job.pluginId,
      filterType: "text",
      filterValue: (job) => job.name || job.pluginName || job.pluginId,
    },
    {
      id: "plugin",
      header: "Plugin",
      cell: (job) => job.pluginName ?? job.pluginId,
    },
    {
      id: "type",
      header: "Type",
      sortField: "jobType",
      cell: (job) => job.jobType,
      filterType: "text",
      filterValue: (job) => job.jobType,
    },
    {
      id: "status",
      header: "Status",
      sortField: "status",
      cell: (job) => <StatusChip status={job.status} />,
      filterType: "select",
      filterOptions: [
        { value: "pending", label: "Pending" },
        { value: "running", label: "Running" },
        { value: "complete", label: "Complete" },
        { value: "failed", label: "Failed" },
      ],
      filterValue: (job) => job.status,
    },
    {
      id: "actions",
      header: "",
      width: 80,
      cell: (job) => (
        <>
          <Tooltip title="View">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/jobs/${job.jobId}`); }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Run again">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/plugins/${job.pluginId}`); }}>
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const table = useTableCollection({ items: jobs, defaultSortField: "createdAt", defaultSortDescending: true });

  return (
    <Box>
      <PageHeader title="Jobs" counter={`(${jobs.length})`} />
      <DataTable
        columns={columns}
        items={table.paginatedItems}
        totalCount={table.totalCount}
        loading={isLoading}
        sortField={table.sortField}
        sortDescending={table.sortDescending}
        onSort={table.onSort}
        page={table.page}
        pageSize={table.pageSize}
        onPageChange={table.onPageChange}
        onPageSizeChange={table.onPageSizeChange}
        onRowClick={(job) => navigate(`/jobs/${job.jobId}`)}
        rowKey={(job) => job.jobId}
        emptyText="No jobs found."
      />
    </Box>
  );
}
